import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["step", "dot", "backBtn", "nextBtn", "submitBtn", "stepCounter", "bgmMode", "headlineField", "ctaField", "platformRatioWarning", "platformRatioWarningText", "mismatchHint", "draftSaveFlag", "removeLogoFlag"];
  static values = { current: { type: Number, default: 0 } };

  static PLATFORM_ASPECT = {
    instagram_reels: "9:16",
    tiktok_video: "9:16",
    youtube_shorts: "9:16",
    website_screens: "16:9",
  };

  static UPLOAD_STEP_INDEX = 1;

  #mismatchPending = false;

  connect() {
    this.#render();
    this.checkPlatformRatio();
  }

  syncNextButton(e) {
    this.#mismatchPending = !!e.detail?.pending;
    this.#render();
  }

  checkPlatformRatio() {
    if (!this.hasPlatformRatioWarningTarget) return;
    const platform = this.element.querySelector('input[name="project[platform]"]:checked')?.value;
    const ratio = this.element.querySelector('input[name="project[aspect_ratio]"]:checked')?.value;
    const expected = this.constructor.PLATFORM_ASPECT[platform];

    if (!platform || !ratio || !expected || expected === ratio) {
      this.platformRatioWarningTarget.hidden = true;
      return;
    }

    const label = this.element.querySelector(`input[name="project[platform]"][value="${platform}"]`)
      ?.closest(".option-card")?.querySelector(".option-card__label")?.textContent?.trim() || platform;

    this.platformRatioWarningTextTarget.textContent = this.platformRatioWarningTarget.dataset.i18nTemplate
      .replace("%{platform}", label)
      .replace("%{expected}", expected)
      .replace("%{chosen}", ratio);
    this.platformRatioWarningTarget.hidden = false;
  }

  next(e) {
    e?.preventDefault();
    if (!this.#validateCurrent()) return;
    if (this.#mismatchGateBlocks(this.currentValue)) return;
    if (this.currentValue < this.stepTargets.length - 1) {
      this.currentValue += 1;
      this.#resetScroll();
    }
  }

  back(e) {
    e?.preventDefault();
    if (this.currentValue > 0) {
      this.currentValue -= 1;
      this.#resetScroll();
    }
  }

  goto(e) {
    const target = parseInt(e.currentTarget.dataset.step, 10);
    if (isNaN(target)) return;
    // Only allow jumping back, or forward if all intermediate steps validate.
    if (target <= this.currentValue) {
      this.currentValue = target;
      this.#resetScroll();
      return;
    }
    while (this.currentValue < target) {
      if (!this.#validateCurrent()) return;
      if (this.#mismatchGateBlocks(this.currentValue)) return;
      this.currentValue += 1;
    }
    this.#resetScroll();
  }

  // Steps differ in height (e.g. step 2's upload UI is much shorter than
  // step 1/3) — swapping `hidden` doesn't move the scroll position, so a
  // step change while scrolled near the bottom of a taller step leaves the
  // page scrolled past the end of the new, shorter one (blank viewport)
  // until the user manually scrolls.
  #resetScroll() {
    window.scrollTo(0, 0);
  }

  currentValueChanged() {
    this.#render();
  }

  toggleHeadlineAi(e) {
    if (this.hasHeadlineFieldTarget) {
      this.headlineFieldTarget.disabled = e.currentTarget.checked;
      if (e.currentTarget.checked) this.headlineFieldTarget.value = "";
    }
  }

  toggleCtaAi(e) {
    if (this.hasCtaFieldTarget) {
      this.ctaFieldTarget.disabled = e.currentTarget.checked;
      if (e.currentTarget.checked) this.ctaFieldTarget.value = "";
    }
  }

  // A stray click here would silently save-and-exit, dropping the user back
  // on the project list with no warning — confirm before that happens, same
  // as the destructive delete action gets. Saving isn't destructive though,
  // so the confirm button stays primary-colored, not the shared dialog's
  // danger-red default.
  markDraft(e) {
    e.preventDefault();
    const btn = e.currentTarget;

    window.dispatchEvent(new CustomEvent("confirm-dialog:open", {
      detail: {
        message: btn.dataset.confirmMessage,
        confirmLabel: btn.dataset.confirmLabel,
        confirmClass: "btn btn--primary",
        onConfirm: () => {
          if (this.hasDraftSaveFlagTarget) this.draftSaveFlagTarget.value = "1";
          this.element.requestSubmit(btn);
        },
      },
    }));
  }

  // Existing assets/logo prefilled from a resumed draft aren't in the
  // upload-preview controller's own file list, so they get their own
  // lightweight remove path: flip the paired disabled hidden field on so
  // it's submitted, then play the same exit motion as a freshly added file.
  removeExistingLogo(e) {
    const item = e.currentTarget.closest(".filmstrip__item");
    if (this.hasRemoveLogoFlagTarget) this.removeLogoFlagTarget.value = "1";
    item?.classList.add("filmstrip__item--exiting");
    item?.addEventListener("transitionend", () => item.remove(), { once: true });
  }

  // Delete button lives in the header, outside any nested form (button_to
  // would need its own <form>, which can't nest inside the wizard's own
  // <form>) — so this builds a throwaway one on confirm and submits it,
  // going through the normal Rails destroy + flash + redirect flow.
  deleteDraft(e) {
    const { deleteUrl, confirmMessage, confirmLabel } = e.currentTarget.dataset;

    window.dispatchEvent(new CustomEvent("confirm-dialog:open", {
      detail: {
        message: confirmMessage,
        confirmLabel,
        onConfirm: () => {
          const form = document.createElement("form");
          form.method = "post";
          form.action = deleteUrl;
          form.style.display = "none";

          const methodInput = document.createElement("input");
          methodInput.type = "hidden";
          methodInput.name = "_method";
          methodInput.value = "delete";

          const csrfInput = document.createElement("input");
          csrfInput.type = "hidden";
          csrfInput.name = "authenticity_token";
          csrfInput.value = document.querySelector('meta[name="csrf-token"]')?.content || "";

          form.append(methodInput, csrfInput);
          document.body.appendChild(form);
          form.submit();
        },
      },
    }));
  }

  selectBgmTrack(e) {
    const mode = e.currentTarget.dataset.bgmMode;
    if (this.hasBgmModeTarget) this.bgmModeTarget.value = mode;
  }

  #render() {
    this.stepTargets.forEach((el, i) => {
      const active = i === this.currentValue;
      el.toggleAttribute("hidden", !active);
      el.setAttribute("aria-hidden", String(!active));
    });
    this.dotTargets.forEach((el, i) => {
      el.classList.toggle("wizard-step--active", i === this.currentValue);
      el.classList.toggle("wizard-step--complete", i < this.currentValue);
    });
    const last = this.currentValue === this.stepTargets.length - 1;
    if (this.hasBackBtnTarget) {
      this.backBtnTarget.toggleAttribute("hidden", this.currentValue === 0);
    }
    if (this.hasNextBtnTarget) {
      // On last step: become a real submit button — remove Stimulus action so
      // the browser handles the click natively and the form submits.
      if (last) {
        this.nextBtnTarget.type = "submit";
        this.nextBtnTarget.removeAttribute("data-action");
        this.nextBtnTarget.textContent = this.nextBtnTarget.dataset.labelSubmit;
      } else {
        this.nextBtnTarget.type = "button";
        this.nextBtnTarget.dataset.action = "click->project-wizard#next";
        this.nextBtnTarget.textContent = this.nextBtnTarget.dataset.labelNext;
      }
      this.nextBtnTarget.disabled = this.#mismatchGateBlocks(this.currentValue);
    }
    if (this.hasMismatchHintTarget) {
      this.mismatchHintTarget.hidden = !this.#mismatchGateBlocks(this.currentValue);
    }
    if (this.hasStepCounterTarget) {
      this.stepCounterTarget.textContent = `${this.currentValue + 1} / ${this.stepTargets.length}`;
    }
    // Notify other controllers (e.g. upload-preview) the step changed so they
    // can refresh their derived state (summary, filmstrip).
    this.dispatch("stepChanged", { detail: { index: this.currentValue } });
  }

  #mismatchGateBlocks(stepIndex) {
    return this.#mismatchPending && stepIndex >= this.constructor.UPLOAD_STEP_INDEX;
  }

  #validateCurrent() {
    const current = this.stepTargets[this.currentValue];
    if (!current) return true;
    const fields = current.querySelectorAll("input, select, textarea");
    for (const field of fields) {
      if (!field.checkValidity()) {
        field.reportValidity();
        return false;
      }
    }
    return true;
  }
}
